/**
 * Stats Cache Service
 *
 * Provides granular caching for stats page data with:
 * - Independent cache entries for each data chunk
 * - Status tracking (idle, loading, loaded, failed)
 * - Priority retry for failed entries
 * - Dependency resolution for rendering
 */

import { Interface, formatUnits, formatEther } from 'ethers'
import { getChainProvider } from '@/lib/chainProvider'
import { getMulticall3Address, BASE_CHAIN, BNB_CHAIN, WELEPHANT_ADDRESSES } from '@/lib/chains'
import { CONTRACT_ADDRESS, GAME_CHAIN_ID, SWAPPER_ADDRESS, STAKING_VAULT_ADDRESS, getMulticall3AddressForChain } from '@/lib/contracts'
import { getTokenPriceUsd, getNativeTokenPriceUsd } from '@/services/tokenPriceService'
import { createMulticallService } from '@/services/multicallService'
import { RaffleMiningABI } from '@/abi/RaffleMiningABI'
import { WelephantSwapABI } from '@/abi/WelephantSwapABI'
import { GameHistoryABI } from '@/abi/GameHistoryABI'
import { GasChargeVaultABI, WelephantVaultABI } from '@/abi/VaultABIs'
import { StakingVaultABI } from '@/abi/StakingVaultABI'
import { StreamVaultABI } from '@/abi/StreamVaultABI'
import { STREAM_VAULT_ADDRESS } from '@/lib/contracts'
import { FEATURES } from '@/config/features'

// ERC20 ABI for supply queries
const ERC20_ABI = [
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
]

// Wormhole Token Bridge addresses
const TOKEN_BRIDGE_ADDRESSES: Record<number, string> = {
  [BASE_CHAIN.id]: '0x8d2de8d2f73F1F4cAB472AC9A881C9b123C79627',
  [BNB_CHAIN.id]: '0xB6F6D86a8f9879A9c87f643768d9efc38c1Da6E7',
}

// Cache entry status
export type CacheStatus = 'idle' | 'loading' | 'loaded' | 'failed'

// Cache entry structure
export interface CacheEntry<T> {
  status: CacheStatus
  data: T | null
  error: string | null
  lastAttempt: number
  retryCount: number
}

// Data types for each cache entry
export interface PricesData {
  basePriceUsd: number | null
  bnbPriceUsd: number | null
  ethPriceUsd: number | null
  bnbNativePriceUsd: number | null
}

export interface ChainTokenData {
  supply: bigint | null
  bridgeEscrow: bigint | null
}

export interface GameAddresses {
  gameHistoryAddress: string
  gasChargeVaultAddress: string
  welephantVaultAddress: string | null
}

export interface GameCoreStats {
  totalRounds: bigint
  totalEthPaidOut: bigint
  totalWelephantPaidOut: bigint
  uniquePlayers: bigint
  paydayFund: bigint
  gasChargePool: bigint
  welephantPriceUsd: number | null
  vaultWelephantAvailable: bigint | null
  vaultEthPending: bigint | null
}

export interface DailyStatsData {
  chartData: DailyChartPoint[]
  aggregated: AggregatedStats
}

export interface DailyChartPoint {
  date: string
  ethFees: number
  welephantBuybacks: number
  plays: number
  uniquePlayers: number
}

export interface AggregatedStats {
  fees24h: bigint
  fees7d: bigint
  fees30d: bigint
  buybacks24h: bigint
  buybacks7d: bigint
  buybacks30d: bigint
  plays24h: bigint
  plays7d: bigint
  plays30d: bigint
  activePlayers24h: bigint
}

export interface SquareWinsData {
  wins: bigint[] // 16 elements, one for each square (1-16, stored at indices 0-15)
}

export interface StreamChartPoint {
  date: string
  deposits: number      // daily USDT deposits (18 decimals, formatted)
}

export interface StreamData {
  totalDonors: bigint       // lifetime unique depositors
  depositsChartData: StreamChartPoint[]
}

export interface StakingChartPoint {
  date: string
  yield: number         // daily yield in WELEPHANT (9 decimals, formatted)
}

export interface StakingData {
  totalAssets: bigint   // WELEPHANT staked (9 decimals)
  totalSupply: bigint   // stWELEPHANT supply (18 decimals)
  apr7d: number         // 7-day APR as percentage
  chartData: StakingChartPoint[]
}

// All cache entries
export interface StatsCache {
  prices: CacheEntry<PricesData>
  baseTokenData: CacheEntry<ChainTokenData>
  bnbTokenData: CacheEntry<ChainTokenData>
  gameAddresses: CacheEntry<GameAddresses>
  gameCoreStats: CacheEntry<GameCoreStats>
  dailyStats: CacheEntry<DailyStatsData>
  squareWins: CacheEntry<SquareWinsData>
  stakingData: CacheEntry<StakingData>
  streamData: CacheEntry<StreamData>
}

// Cache entry keys
export type CacheKey = keyof StatsCache

// Initial cache entry
function createInitialEntry<T>(): CacheEntry<T> {
  return {
    status: 'idle',
    data: null,
    error: null,
    lastAttempt: 0,
    retryCount: 0,
  }
}

// Create initial cache
export function createInitialCache(): StatsCache {
  return {
    prices: createInitialEntry<PricesData>(),
    baseTokenData: createInitialEntry<ChainTokenData>(),
    bnbTokenData: createInitialEntry<ChainTokenData>(),
    gameAddresses: createInitialEntry<GameAddresses>(),
    gameCoreStats: createInitialEntry<GameCoreStats>(),
    dailyStats: createInitialEntry<DailyStatsData>(),
    squareWins: createInitialEntry<SquareWinsData>(),
    stakingData: createInitialEntry<StakingData>(),
    streamData: createInitialEntry<StreamData>(),
  }
}

// Check if error is transient (worth retrying)
function isTransientError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()
  return (
    msg.includes('timeout') ||
    msg.includes('network') ||
    msg.includes('econnrefused') ||
    msg.includes('econnreset') ||
    msg.includes('rate limit') ||
    msg.includes('429') ||
    msg.includes('503') ||
    msg.includes('missing revert data')
  )
}

// Retry delays (exponential backoff)
const RETRY_DELAYS = [2000, 4000, 8000, 16000] // 2s, 4s, 8s, 16s
const MAX_RETRIES = 4

// Get retry delay based on retry count
export function getRetryDelay(retryCount: number): number {
  return RETRY_DELAYS[Math.min(retryCount, RETRY_DELAYS.length - 1)]
}

// Check if entry should be retried
export function shouldRetry(entry: CacheEntry<unknown>): boolean {
  if (entry.status !== 'failed') return false
  if (entry.retryCount >= MAX_RETRIES) return false
  const delay = getRetryDelay(entry.retryCount)
  return Date.now() - entry.lastAttempt >= delay
}

// Dependencies between cache entries
const DEPENDENCIES: Partial<Record<CacheKey, CacheKey[]>> = {
  gameCoreStats: ['gameAddresses'],
  dailyStats: ['gameAddresses'],
  squareWins: ['gameAddresses'],
}

// Check if all dependencies are loaded
export function areDependenciesLoaded(cache: StatsCache, key: CacheKey): boolean {
  const deps = DEPENDENCIES[key]
  if (!deps) return true
  return deps.every(dep => cache[dep].status === 'loaded')
}

// Cache entries to fetch. Feature-flagged entries are excluded so their
// on-chain calls never fire: the game entries feed the mining sections of
// /stats, and streamData feeds stream stats.
const FETCH_KEYS: CacheKey[] = [
  'prices', 'baseTokenData', 'bnbTokenData', 'stakingData',
  ...(FEATURES.mining ? (['gameAddresses', 'gameCoreStats', 'dailyStats', 'squareWins'] as CacheKey[]) : []),
  ...(FEATURES.streams ? (['streamData'] as CacheKey[]) : []),
]

// Get entries that are ready to fetch (idle or should retry, with deps loaded)
export function getReadyToFetch(cache: StatsCache): CacheKey[] {
  return FETCH_KEYS.filter(key => {
    const entry = cache[key]
    const depsLoaded = areDependenciesLoaded(cache, key)

    // Ready if idle and deps loaded
    if (entry.status === 'idle' && depsLoaded) return true

    // Ready if failed, should retry, and deps loaded
    if (entry.status === 'failed' && shouldRetry(entry) && depsLoaded) return true

    return false
  })
}

// Get failed entries that need retry (prioritized)
export function getFailedEntries(cache: StatsCache): CacheKey[] {
  return FETCH_KEYS.filter(key => cache[key].status === 'failed')
}

// Fetch functions for each cache entry

export async function fetchPrices(): Promise<PricesData> {
  const [basePriceUsd, bnbPriceUsd, ethPriceUsd, bnbNativePriceUsd] = await Promise.all([
    getTokenPriceUsd('WELEPHANT', BASE_CHAIN.id),
    getTokenPriceUsd('WELEPHANT', BNB_CHAIN.id),
    getNativeTokenPriceUsd(BASE_CHAIN.id),
    getNativeTokenPriceUsd(BNB_CHAIN.id),
  ])

  return { basePriceUsd, bnbPriceUsd, ethPriceUsd, bnbNativePriceUsd }
}

export async function fetchChainTokenData(chainId: number): Promise<ChainTokenData> {
  const provider = await getChainProvider(chainId)
  const multicallAddr = getMulticall3Address(chainId)
  if (!multicallAddr) throw new Error(`No Multicall3 for chain ${chainId}`)

  const multicall = createMulticallService(provider, multicallAddr, chainId)
  const erc20Iface = new Interface(ERC20_ABI)
  const tokenAddress = WELEPHANT_ADDRESSES[chainId]
  const bridgeAddress = TOKEN_BRIDGE_ADDRESSES[chainId]

  const calls = [
    multicall.encodeCall(tokenAddress, erc20Iface, 'totalSupply', [], true),
    multicall.encodeCall(tokenAddress, erc20Iface, 'balanceOf', [bridgeAddress], true),
  ]

  const [supply, bridgeEscrow] = await multicall.callAndDecode<[bigint | null, bigint | null]>(calls)

  return { supply, bridgeEscrow }
}

export async function fetchGameAddresses(): Promise<GameAddresses> {
  if (!CONTRACT_ADDRESS || CONTRACT_ADDRESS === '0x0000000000000000000000000000000000000000') {
    throw new Error('Contract not configured')
  }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)
  const raffleMiningIface = new Interface(RaffleMiningABI)

  const calls = [
    multicall.encodeCall(CONTRACT_ADDRESS, raffleMiningIface, 'gameHistory'),
    multicall.encodeCall(CONTRACT_ADDRESS, raffleMiningIface, 'gasChargeVault'),
    multicall.encodeCall(CONTRACT_ADDRESS, raffleMiningIface, 'welephantVault', [], true),
  ]

  const [gameHistoryAddress, gasChargeVaultAddress, welephantVaultAddress] =
    await multicall.callAndDecode<[string, string, string | null]>(calls)

  return { gameHistoryAddress, gasChargeVaultAddress, welephantVaultAddress }
}

export async function fetchGameCoreStats(addresses: GameAddresses): Promise<GameCoreStats> {
  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

  const gameHistoryIface = new Interface(GameHistoryABI)
  const gasChargeVaultIface = new Interface(GasChargeVaultABI)
  const welephantVaultIface = new Interface(WelephantVaultABI)
  const swapIface = new Interface(WelephantSwapABI)

  const calls = [
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'currentRoundId'),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'totalEthPaidOut'),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'totalWelephantPaidOut'),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'totalUniquePlayers'),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'paydayFund'),
    multicall.encodeCall(addresses.gasChargeVaultAddress, gasChargeVaultIface, 'gasChargePool'),
    multicall.encodeCall(SWAPPER_ADDRESS, swapIface, 'getWelephantPriceInUSD', [], true),
  ]

  // Add vault calls if configured
  if (addresses.welephantVaultAddress && addresses.welephantVaultAddress !== '0x0000000000000000000000000000000000000000') {
    calls.push(
      multicall.encodeCall(addresses.welephantVaultAddress, welephantVaultIface, 'welephantAvailable', [], true),
      multicall.encodeCall(addresses.welephantVaultAddress, welephantVaultIface, 'reservesAvailable', [], true),
    )
  }

  const results = await multicall.callAndDecode<(bigint | null)[]>(calls)

  const [totalRounds, totalEthPaidOut, totalWelephantPaidOut, uniquePlayers, paydayFund, gasChargePool, priceInUsd] =
    results.slice(0, 7) as [bigint, bigint, bigint, bigint, bigint, bigint, bigint | null]

  const vaultWelephantAvailable = results.length > 7 ? (results[7] as bigint | null) : null
  const vaultEthPending = results.length > 8 ? (results[8] as bigint | null) : null

  const welephantPriceUsd = priceInUsd ? parseFloat(formatUnits(priceInUsd, 18)) : null

  return {
    totalRounds,
    totalEthPaidOut,
    totalWelephantPaidOut,
    uniquePlayers,
    paydayFund,
    gasChargePool,
    welephantPriceUsd,
    vaultWelephantAvailable,
    vaultEthPending,
  }
}

export async function fetchDailyStats(addresses: GameAddresses): Promise<DailyStatsData> {
  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)
  const gameHistoryIface = new Interface(GameHistoryABI)

  const calls = [
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getDailyStatsForChart', [31]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedEthFees', [1]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedEthFees', [7]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedEthFees', [30]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedWelephantBuybacks', [1]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedWelephantBuybacks', [7]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedWelephantBuybacks', [30]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedPlays', [1]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedPlays', [7]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAggregatedPlays', [30]),
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getActivePlayers24h'),
  ]

  const [
    rawChartData,
    fees1d, fees7d, fees30d,
    buybacks1d, buybacks7d, buybacks30d,
    plays1d, plays7d, plays30d,
    activePlayers24h
  ] = await multicall.callAndDecode<[
    [bigint[], bigint[], bigint[], bigint[], bigint[]],
    bigint, bigint, bigint,
    bigint, bigint, bigint,
    bigint, bigint, bigint,
    bigint
  ]>(calls)

  // Transform chart data
  const [dayIndices, ethFees, welephantBuybacks, plays, uniquePlayers] = rawChartData
  const chartData: DailyChartPoint[] = dayIndices.map((dayIndex: bigint, i: number) => {
    const date = new Date(Number(dayIndex) * 86400 * 1000)
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      ethFees: parseFloat(formatEther(ethFees[i])),
      welephantBuybacks: parseFloat(formatUnits(welephantBuybacks[i], 9)),
      plays: Number(plays[i]),
      uniquePlayers: Number(uniquePlayers[i]),
    }
  })

  return {
    chartData: import.meta.env.DEV ? chartData : chartData.slice(0, -1),
    aggregated: {
      fees24h: fees1d,
      fees7d: fees7d,
      fees30d: fees30d,
      buybacks24h: buybacks1d,
      buybacks7d: buybacks7d,
      buybacks30d: buybacks30d,
      plays24h: plays1d,
      plays7d: plays7d,
      plays30d: plays30d,
      activePlayers24h: activePlayers24h,
    },
  }
}

export async function fetchSquareWins(addresses: GameAddresses): Promise<SquareWinsData> {
  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)
  const gameHistoryIface = new Interface(GameHistoryABI)

  const calls = [
    multicall.encodeCall(addresses.gameHistoryAddress, gameHistoryIface, 'getAllLifetimeSquareWins'),
  ]

  const [wins] = await multicall.callAndDecode<[bigint[]]>(calls)

  return { wins: Array.from(wins) }
}

export async function fetchStakingData(): Promise<StakingData> {
  if (!STAKING_VAULT_ADDRESS || STAKING_VAULT_ADDRESS === '0x0000000000000000000000000000000000000000') {
    throw new Error('Staking vault not configured')
  }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)
  const vaultIface = new Interface(StakingVaultABI)

  const calls = [
    multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'totalAssets'),
    multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'totalSupply'),
    multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getAPR', [7], true),
    multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getDailyYieldForChart', [31], true),
  ]

  const [totalAssets, totalSupply, apr7dBps, rawChartData] = await multicall.callAndDecode<[bigint, bigint, bigint | null, [bigint[], bigint[]] | null]>(calls)

  // Transform chart data
  let chartData: StakingChartPoint[] = []
  if (rawChartData) {
    const [dayIndices, yields] = rawChartData
    chartData = dayIndices.map((dayIndex: bigint, i: number) => {
      const date = new Date(Number(dayIndex) * 86400 * 1000)
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        yield: parseFloat(formatUnits(yields[i], 9)),
      }
    })
  }

  return {
    totalAssets,
    totalSupply,
    apr7d: Number(apr7dBps ?? 0n) / 100,
    chartData: import.meta.env.DEV ? chartData : chartData.slice(0, -1),
  }
}

export async function fetchStreamData(): Promise<StreamData> {
  if (!STREAM_VAULT_ADDRESS || STREAM_VAULT_ADDRESS === '0x0000000000000000000000000000000000000000') {
    throw new Error('Stream vault not configured')
  }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)
  const vaultIface = new Interface(StreamVaultABI)

  const calls = [
    multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'totalDonors'),
    multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'getDailyDepositsForChart', [31], true),
  ]

  const [totalDonors, rawDepositsChart] = await multicall.callAndDecode<[bigint, [bigint[], bigint[]] | null]>(calls)

  // Transform deposits chart data
  let depositsChartData: StreamChartPoint[] = []
  if (rawDepositsChart) {
    const [dayIndices, deposits] = rawDepositsChart
    depositsChartData = dayIndices.map((dayIndex: bigint, i: number) => {
      const date = new Date(Number(dayIndex) * 86400 * 1000)
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        deposits: parseFloat(formatUnits(deposits[i], 18)),
      }
    })
  }

  return {
    totalDonors,
    depositsChartData: import.meta.env.DEV ? depositsChartData : depositsChartData.slice(0, -1),
  }
}

// Generic fetch wrapper that handles errors
export async function fetchCacheEntry<T>(
  key: CacheKey,
  fetchFn: () => Promise<T>,
  currentEntry: CacheEntry<T>,
  onUpdate: (entry: CacheEntry<T>) => void
): Promise<void> {
  // Update to loading
  onUpdate({
    ...currentEntry,
    status: 'loading',
    lastAttempt: Date.now(),
  })

  try {
    const data = await fetchFn()
    onUpdate({
      status: 'loaded',
      data,
      error: null,
      lastAttempt: Date.now(),
      retryCount: 0,
    })
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err)
    const isTransient = isTransientError(err)

    // Log appropriately
    if (isTransient) {
      console.warn(`[StatsCache] ${key} temporarily unavailable, will retry`)
    } else {
      console.error(`[StatsCache] ${key} failed:`, err)
    }

    onUpdate({
      status: 'failed',
      data: currentEntry.data, // Keep old data if available
      error: errorMsg,
      lastAttempt: Date.now(),
      retryCount: currentEntry.retryCount + 1,
    })
  }
}
